/**
 * SpineManager — singleton for loading, caching, pooling, and playing
 * Spine 3.8 skeletons via @esotericsoftware/spine-pixi-v8.
 *
 * Importing spine-pixi-v8 registers PixiJS asset loaders for .json/.skel and
 * .atlas files automatically, so we use `Assets.load` + `Spine.from`.
 */

import { Assets } from 'pixi.js';
import { Spine } from '@esotericsoftware/spine-pixi-v8';
import type { TrackEntry } from '@esotericsoftware/spine-core';
import { Logger } from '../utils/Logger';

const logger = new Logger('SpineManager');

/** Names of skeletons that use path-type attachments (suppress warnings) */
const PATH_ATTACHMENT_SKELETONS = new Set([
  'bonus_pick_orbs_fly',
  'fx_to_center_and_panels',
]);

/** All known skeleton names for bulk preloading */
export const ALL_SKELETON_NAMES: readonly string[] = [
  '7rush_fg_back',
  'big_win',
  'bonus_pick_background',
  'bonus_pick_orbs_fly',
  'bonus_pick_orbs_pick',
  'bonus_pick_orbs_sheen',
  'buttions_popup_buy_a_feature',
  'collect_frame',
  'collect_trail',
  'fx_to_center_and_panels',
  'jp_panel_square',
  'jp_panel_square_pm',
  'jp_panel_wide',
  'jp_panel_wide_pm',
  'pannels_loop',
  'pic_a',
  'pic_b',
  'pic_c',
  'pic_d',
  'pic_e',
  'pile_diamond',
  'popup_buy_feature',
  'r_7s_d_royals',
  'r7s_popup_backgrounds',
  'r7s_popup_text',
  'rush_fever_symbol',
  'rush_fever_value_square',
  'rush_fever_value_square_pm',
  'rush_fever_value_wide',
  'rush_fever_value_wide_mobile',
  'rush_fever_value_wide_mobile_pm',
  'scatter',
  'wild',
  'zrf_rushfever_popup_bckgrnd',
  'zrf_rushfever_popup_text',
] as const;

/** Mapping from symbol IDs to their Spine skeleton + animation */
export interface SymbolSpineMapping {
  skeleton: string;
  animation: string;
}

/** Map symbol IDs to the Spine skeleton/animation to play on win */
export const SYMBOL_WIN_ANIMS: Record<string, SymbolSpineMapping> = {
  WILD:    { skeleton: 'wild',           animation: 'win' },
  SCATTER: { skeleton: 'scatter',        animation: 'win' },
  PICa:    { skeleton: 'pic_a',          animation: 'animation' },
  PICb:    { skeleton: 'pic_b',          animation: 'animation' },
  PICc:    { skeleton: 'pic_c',          animation: 'animation' },
  PICd:    { skeleton: 'pic_d',          animation: 'animation' },
  PICe:    { skeleton: 'pic_e',          animation: 'animation' },
  A:       { skeleton: 'r_7s_d_royals',  animation: 'pip_a' },
  K:       { skeleton: 'r_7s_d_royals',  animation: 'pip_b' },
  Q:       { skeleton: 'r_7s_d_royals',  animation: 'pip_c' },
  J:       { skeleton: 'r_7s_d_royals',  animation: 'pip_d' },
  T:       { skeleton: 'r_7s_d_royals',  animation: 'pip_e' },
};

/** Map symbol IDs to their Spine hit (land) animation */
export const SYMBOL_HIT_ANIMS: Record<string, SymbolSpineMapping> = {
  WILD:    { skeleton: 'wild',    animation: 'hit' },
  SCATTER: { skeleton: 'scatter', animation: 'hit' },
};

/** Central cache and factory for Spine skeletons */
export class SpineManager {
  private static _instance: SpineManager | null = null;

  /** Set of skeleton names that have been loaded into PixiJS Assets */
  private _loaded: Set<string> = new Set();

  /** Set of skeleton names that failed to load (won't retry) */
  private _failed: Set<string> = new Set();

  /** Instance pool keyed by skeleton name */
  private _pool: Map<string, Spine[]> = new Map();

  // ── Singleton ──────────────────────────────────────────────────────────

  static getInstance(): SpineManager {
    if (!SpineManager._instance) {
      SpineManager._instance = new SpineManager();
    }
    return SpineManager._instance;
  }

  private constructor() {
    // singleton
  }

  // ── Loading ────────────────────────────────────────────────────────────

  /**
   * Load a single skeleton's JSON + atlas + textures into the PixiJS asset
   * cache.  Safe to call multiple times for the same name (no-ops if
   * already loaded or failed).
   */
  async loadSkeleton(name: string): Promise<boolean> {
    if (this._loaded.has(name) || this._failed.has(name)) {
      return this._loaded.has(name);
    }

    const basePath = `assets/animations/${name}/${name}`;

    try {
      logger.info(`Loading skeleton: ${name}`);

      // Register skeleton + atlas as a bundle so PixiJS resolves them
      // The spine-pixi-v8 loaders handle .json → SkeletonData and
      // .atlas → TextureAtlas automatically.
      await Assets.load([
        { alias: `${name}-skeleton`, src: `${basePath}.json` },
        { alias: `${name}-atlas`, src: `${basePath}.atlas` },
      ]);

      this._loaded.add(name);
      logger.info(`Skeleton loaded: ${name}`);
      return true;
    } catch (err) {
      this._failed.add(name);
      logger.warn(`Failed to load skeleton "${name}": ${err}`);
      return false;
    }
  }

  /**
   * Preload an array of skeleton names.
   * Failures are logged and silently skipped.
   */
  async preloadSkeletons(names: readonly string[]): Promise<void> {
    const results = await Promise.allSettled(
      names.map((n) => this.loadSkeleton(n)),
    );

    let successCount = 0;
    for (const r of results) {
      if (r.status === 'fulfilled' && r.value) successCount++;
    }
    logger.info(
      `Preload complete: ${successCount}/${names.length} skeletons loaded`,
    );
  }

  /** Preload every known skeleton */
  async preloadAll(): Promise<void> {
    await this.preloadSkeletons(ALL_SKELETON_NAMES);
  }

  /** Check whether a skeleton is loaded and ready to instantiate */
  isLoaded(name: string): boolean {
    return this._loaded.has(name);
  }

  // ── Instance creation ──────────────────────────────────────────────────

  /**
   * Create (or retrieve from pool) a Spine display object for the given
   * skeleton.  Returns `null` if the skeleton hasn't been loaded.
   */
  createInstance(name: string): Spine | null {
    if (!this._loaded.has(name)) {
      logger.warn(`Cannot create instance — skeleton "${name}" not loaded`);
      return null;
    }

    // Check pool first
    const pool = this._pool.get(name);
    if (pool && pool.length > 0) {
      const instance = pool.pop()!;
      instance.visible = true;
      instance.alpha = 1;
      instance.skeleton.setToSetupPose();
      instance.state.clearTracks();
      return instance;
    }

    // Create new instance via Spine.from (synchronous — assets already cached)
    try {
      const basePath = `assets/animations/${name}/${name}`;
      const instance = Spine.from({
        skeleton: `${name}-skeleton`,
        atlas: `${name}-atlas`,
      });

      // Suppress missing attachment warnings for path-type skeletons
      if (PATH_ATTACHMENT_SKELETONS.has(name)) {
        // These skeletons use path constraints that don't have image data
        logger.debug(`Instance created (path-type): ${name}`);
      } else {
        logger.debug(`Instance created: ${name}`);
      }

      return instance;
    } catch (err) {
      logger.warn(`Failed to create Spine instance "${name}": ${err}`);
      return null;
    }
  }

  // ── Animation playback ─────────────────────────────────────────────────

  /**
   * Play an animation on a Spine instance.
   *
   * - If `loop` is true, sets the animation and resolves immediately.
   * - If `loop` is false, resolves when the animation completes.
   *
   * Returns the TrackEntry or null on failure.
   */
  async playAnimation(
    instance: Spine,
    animName: string,
    loop: boolean = false,
  ): Promise<TrackEntry | null> {
    try {
      const entry = instance.state.setAnimation(0, animName, loop);

      if (loop) {
        // Looping — resolve immediately
        return entry;
      }

      // One-shot — wait for completion
      return await new Promise<TrackEntry>((resolve) => {
        const onComplete = (_trackEntry: TrackEntry) => {
          instance.state.removeListener(listener);
          resolve(_trackEntry);
        };
        const listener = { complete: onComplete } as any;
        instance.state.addListener(listener);
      });
    } catch (err) {
      logger.warn(
        `Failed to play animation "${animName}" on Spine instance: ${err}`,
      );
      return null;
    }
  }

  /**
   * Queue an animation after the current one finishes on track 0.
   */
  queueAnimation(
    instance: Spine,
    animName: string,
    loop: boolean = false,
    delay: number = 0,
  ): TrackEntry | null {
    try {
      return instance.state.addAnimation(0, animName, loop, delay);
    } catch (err) {
      logger.warn(`Failed to queue animation "${animName}": ${err}`);
      return null;
    }
  }

  /**
   * Play a start animation (one-shot), then automatically transition to a
   * loop animation.  Resolves after the start animation completes.
   */
  async playStartThenLoop(
    instance: Spine,
    startAnim: string,
    loopAnim: string,
  ): Promise<void> {
    try {
      instance.state.setAnimation(0, startAnim, false);
      instance.state.addAnimation(0, loopAnim, true, 0);

      // Wait for the start animation to finish
      await new Promise<void>((resolve) => {
        const listener = {
          complete: (entry: TrackEntry) => {
            if (entry.animation?.name === startAnim) {
              instance.state.removeListener(listener as any);
              resolve();
            }
          },
        };
        instance.state.addListener(listener as any);
      });
    } catch (err) {
      logger.warn(`Failed playStartThenLoop "${startAnim}" → "${loopAnim}": ${err}`);
    }
  }

  // ── Pooling / cleanup ──────────────────────────────────────────────────

  /**
   * Return a Spine instance to the pool for later reuse.
   * The caller must remove it from its parent container first.
   */
  returnInstance(name: string, instance: Spine): void {
    instance.state.clearTracks();
    instance.skeleton.setToSetupPose();
    instance.visible = false;

    if (instance.parent) {
      instance.parent.removeChild(instance);
    }

    let pool = this._pool.get(name);
    if (!pool) {
      pool = [];
      this._pool.set(name, pool);
    }
    pool.push(instance);
    logger.debug(`Instance returned to pool: ${name} (pool size: ${pool.length})`);
  }

  /**
   * Destroy a Spine instance permanently (not pooled).
   */
  destroyInstance(instance: Spine): void {
    if (instance.parent) {
      instance.parent.removeChild(instance);
    }
    instance.destroy();
  }

  /** Clear all pools */
  clearPools(): void {
    for (const [name, pool] of this._pool) {
      for (const inst of pool) {
        inst.destroy();
      }
      logger.debug(`Pool cleared: ${name} (${pool.length} instances destroyed)`);
    }
    this._pool.clear();
  }
}
